/**
 * DekInput - 12-character DEK in 3 parts (4-4-4).
 * Must include at least one: uppercase, lowercase, number, symbol.
 * Single show/hide toggle after the last box reveals or masks all three at once.
 */

import React, { useState } from 'react';
import './DekInput.css';

const DEK_PART_LENGTH = 4;
const SYMBOL_REGEX = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/;

export function validateDek(value: string): string | null {
  if (value.length !== 12) return 'マスターキーは12文字で入力してください。';
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /[0-9]/.test(value);
  const hasSymbol = SYMBOL_REGEX.test(value);
  if (!hasUpper) return '大文字を1文字以上含めてください。';
  if (!hasLower) return '小文字を1文字以上含めてください。';
  if (!hasNumber) return '数字を1文字以上含めてください。';
  if (!hasSymbol) return '記号を1文字以上含めてください。';
  return null;
}

interface DekInputProps {
  part1: string;
  part2: string;
  part3: string;
  onPart1Change: (v: string) => void;
  onPart2Change: (v: string) => void;
  onPart3Change: (v: string) => void;
  error?: string;
  disabled?: boolean;
  showHint?: boolean;
  'aria-label'?: string;
}

export const DekInput: React.FC<DekInputProps> = ({
  part1,
  part2,
  part3,
  onPart1Change,
  onPart2Change,
  onPart3Change,
  error,
  disabled = false,
  showHint = true,
  'aria-label': ariaLabel = 'マスターキー'
}) => {
  const [showValue, setShowValue] = useState(false);
  const clamp = (v: string, max: number) => v.slice(0, max);
  const display = (value: string) => (showValue ? value : '•'.repeat(value.length));

  const makeChangeHandler = (
    part: string,
    setPart: (v: string) => void
  ) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (showValue) setPart(clamp(e.target.value, DEK_PART_LENGTH));
  };

  const makeKeyDownHandler = (
    part: string,
    setPart: (v: string) => void
  ) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showValue) return;
    e.preventDefault();
    if (e.key === 'Backspace') {
      setPart(part.slice(0, -1));
    } else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      setPart(clamp(part + e.key, DEK_PART_LENGTH));
    }
  };

  const handlePart1Paste = (e: React.ClipboardEvent) => {
    if (showValue) return;
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').slice(0, DEK_PART_LENGTH - part1.length);
    onPart1Change(clamp(part1 + pasted, DEK_PART_LENGTH));
  };
  const handlePart2Paste = (e: React.ClipboardEvent) => {
    if (showValue) return;
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').slice(0, DEK_PART_LENGTH - part2.length);
    onPart2Change(clamp(part2 + pasted, DEK_PART_LENGTH));
  };
  const handlePart3Paste = (e: React.ClipboardEvent) => {
    if (showValue) return;
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').slice(0, DEK_PART_LENGTH - part3.length);
    onPart3Change(clamp(part3 + pasted, DEK_PART_LENGTH));
  };

  return (
    <div className="dek-input-wrap" role="group" aria-label={ariaLabel}>
      <div className="dek-input-parts">
        <input
          type="text"
          className="dek-input-part"
          value={display(part1)}
          onChange={makeChangeHandler(part1, onPart1Change)}
          onKeyDown={makeKeyDownHandler(part1, onPart1Change)}
          onPaste={handlePart1Paste}
          maxLength={DEK_PART_LENGTH}
          disabled={disabled}
          autoComplete="off"
          placeholder="••••"
          aria-label="マスターキー 1つ目"
          inputMode={showValue ? 'text' : 'none'}
        />
        <span className="dek-input-sep" aria-hidden="true">-</span>
        <input
          type="text"
          className="dek-input-part"
          value={display(part2)}
          onChange={makeChangeHandler(part2, onPart2Change)}
          onKeyDown={makeKeyDownHandler(part2, onPart2Change)}
          onPaste={handlePart2Paste}
          maxLength={DEK_PART_LENGTH}
          disabled={disabled}
          autoComplete="off"
          placeholder="••••"
          aria-label="マスターキー 2つ目"
          inputMode={showValue ? 'text' : 'none'}
        />
        <span className="dek-input-sep" aria-hidden="true">-</span>
        <input
          type="text"
          className="dek-input-part"
          value={display(part3)}
          onChange={makeChangeHandler(part3, onPart3Change)}
          onKeyDown={makeKeyDownHandler(part3, onPart3Change)}
          onPaste={handlePart3Paste}
          maxLength={DEK_PART_LENGTH}
          disabled={disabled}
          autoComplete="off"
          placeholder="••••"
          aria-label="マスターキー 3つ目"
          inputMode={showValue ? 'text' : 'none'}
        />
        <button
          type="button"
          className="dek-input-toggle"
          onClick={() => setShowValue((v) => !v)}
          disabled={disabled}
          title={showValue ? '隠す' : '表示'}
          aria-label={showValue ? 'マスターキーを隠す' : 'マスターキーを表示'}
        >
          {showValue ? '隠す' : '表示'}
        </button>
      </div>
      {showHint && (
        <p className="dek-input-hint">
          大文字・小文字・数字・記号をそれぞれ1文字以上含む12文字を、3つに分けて入力（例: Ab1!-Cd2@-Ef3#）
        </p>
      )}
      {error && <p className="dek-input-error" role="alert">{error}</p>}
    </div>
  );
};
